import {
	ImagePlus,
	PanelLeftOpen,
	PencilLine,
	SendHorizonal,
} from "lucide-react";
import logo from "@/assets/logo-mini-2.svg";
import Image from "@rasenganjs/image";
import {
	Tooltip,
	TooltipContent,
	TooltipProvider,
	TooltipTrigger,
} from "@/components/atoms/Tooltip";
import NavigationContext from "@/contexts/navigation";
import {
	useContext,
	useEffect,
	useMemo,
	useRef,
	useState,
} from "react";
import { twMerge } from "tailwind-merge";
import { useActions, useOperations, useSignal } from "@rasenganjs/signals";
import {
	ChatsActions,
	ChatsOperations,
	ChatsState,
} from "@/store/signals/chats";
import Message from "@/entities/Message";
import Bot from "@/api/model/bot";
import { ChatSession } from "@google/generative-ai";
import Chat from "@/entities/Chat";

interface Props {
	children?: React.ReactNode;
}

export default function Main({ children }: Props) {
	const { open, toggleDrawer } = useContext(NavigationContext);

	// Global state
	const {
		addMessage,
		addResponse,
		create,
		reset,
		renameChat,
		stopGenerating,
		updatePrompt,
	} = useActions<ChatsActions>("chats");
	const { isGenerating, selectedChatId, prompt } =
		useSignal<ChatsState>("chats");
	const { getCurrentChat } = useOperations<ChatsOperations>("chats");

	// Local state
	const [message, setMessage] = useState("");
	const [chatbot, setChatbot] = useState<ChatSession | null>(null);
	const [loadingResult, setLoadingResult] = useState(false);

	const chat = useMemo(() => {
		return getCurrentChat(selectedChatId);
	}, [selectedChatId]);

	const inputRef = useRef<HTMLDivElement>(null);

	// Effects
	// Start chat
	useEffect(() => {
		inputRef.current?.focus();

		if (!chat) return;

		const chatbot = Bot.startChat(chat.messages);
		setChatbot(chatbot);
	}, [selectedChatId]);

	useEffect(() => {
		let timer: any;

		if (isGenerating) {
			setLoadingResult(true);
		} else {
			timer = setTimeout(() => {
				setLoadingResult(false);
			}, 1000);
		}

		return () => {
			clearTimeout(timer);
		};
	}, [isGenerating]);

	useEffect(() => {
		if (!isGenerating || !chatbot || !chat) return;

		if (!loadingResult) {
			return;
		}

		(async () => {
			try {
				const id = `${Date.now().toString()}`;
				addResponse({
					chatId: chat.id,
					messageId: id,
					content: "",
				});

				// 1. Get the response from the chatbot
				const result = await chatbot.sendMessageStream(prompt);

				// 2. Add the response to the chat
				for await (const chunk of result.stream) {
					const chunkText = chunk.text();

					addResponse({
						chatId: chat.id,
						messageId: id,
						content: chunkText,
					});
				}

				// Generate a chat name if not yet
				if (chat.name === "") {
					const chatName = await Bot.generateChatName(prompt);

					// Update chat name
					renameChat({
						chatId: chat.id,
						name: chatName,
					});
				}
			} catch (error: any) {
				// console.log(error);
			} finally {
				stopGenerating();
				setLoadingResult(false);
			}
		})();
	}, [isGenerating, loadingResult, chatbot]);

	// Listen to text input field event
	useEffect(() => {
		if (!inputRef.current) return;

		const handleKeyDown = async (e: KeyboardEvent) => {
			if (!inputRef.current) return;

			if (e.key === "Enter" && e.shiftKey) {
				return;
			}

			if (e.key === "Enter") {
				e.preventDefault();

				handleSendMessage();
				return;
			}
		};

		inputRef.current.addEventListener("keydown", handleKeyDown);

		return () => {
			inputRef.current?.removeEventListener("keydown", handleKeyDown);
		};
	}, [chatbot, selectedChatId]);

	useEffect(() => {
		if (!isGenerating || !chatbot || !chat) return;

		if (!isGenerating) {
			return;
		}

		const timer = setInterval(() => {
			// Scroll to the bottom smoothly
			const chatContainer = document.querySelector(".h-content-inner");

			if (chatContainer) {
				chatContainer.scrollTo({
					top: chatContainer.scrollHeight,
					behavior: "smooth",
				});
			}
		}, 100);

		return () => clearInterval(timer);
	}, [isGenerating]);

	useEffect(() => {
		// Scroll to the bottom smoothly
		const chatContainer = document.querySelector(".h-content-inner");

		if (chatContainer) {
			chatContainer.scrollTo({
				top: chatContainer.scrollHeight,
				behavior: "smooth",
			});
		}
	}, [chat?.messages.length]);

	// Handlers
	const handleSendMessage = () => {
		if (!inputRef.current) return;
		const message = inputRef.current.innerHTML?.trim();
		const prompt = inputRef.current.textContent?.trim();

		if (!message) return;

		if (!selectedChatId) {
			// Create a new message
			const messageId = `message-${Date.now()}`;
			const newMessage = new Message({
				id: messageId,
				role: "user",
				content: message,
				createdAt: new Date(Date.now()),
			});

			// Create a new chat
			const id = `chat-${Date.now()}`;
			const chat = new Chat({ id, messages: [newMessage] });

			// Save chat
			create(chat);

			// Update prompt
			updatePrompt(prompt || "");
			setMessage("");
			inputRef.current.textContent = "";

			return;
		}

		addMessage({
			chatId: selectedChatId,
			message: new Message({
				id: `message-${Date.now()}`,
				role: "user",
				content: message,
				createdAt: new Date(Date.now()),
			}),
		});

		updatePrompt(message);
		setMessage("");
		inputRef.current.textContent = "";
	};

	return (
		<main className='relative w-full h-screen md:h-content text-foreground bg-secondary md:rounded-3xl overflow-hidden transition-all duration-300 '>
			<section className='z-20 absolute left-0 right-0 top-0 h-[60px] font-outfitBold backdrop-blur-md flex items-center justify-center bg-secondary/70 gap-2'>
				<div
					className={twMerge(
						"transition-all duration-300 items-center justify-between absolute top-1/2 -translate-y-1/2 left-4 gap-4",
						open ? "hidden" : "flex"
					)}
				>
					<TooltipProvider>
						<Tooltip>
							<TooltipTrigger aria-label='Toggle sidebar'>
								<div
									onClick={toggleDrawer}
									className='w-10 h-10 flex items-center justify-center rounded-full bg-card/70 cursor-pointer'
								>
									<PanelLeftOpen size={20} />
								</div>
							</TooltipTrigger>
							<TooltipContent>
								<p>Close sidebar</p>
							</TooltipContent>
						</Tooltip>
					</TooltipProvider>

					<TooltipProvider>
						<Tooltip>
							<TooltipTrigger aria-label='New chat'>
								<div
									onClick={reset}
									className='w-10 h-10 flex items-center justify-center rounded-full bg-card/70 cursor-pointer'
								>
									<PencilLine size={20} />
								</div>
							</TooltipTrigger>
							<TooltipContent>
								<p>New chat</p>
							</TooltipContent>
						</Tooltip>
					</TooltipProvider>
				</div>

				<Image src={logo} alt='RasenganAI' width={20} height={"auto"} />
				<span>Rasengan AI</span>
			</section>

			<div className='h-content-inner overflow-auto max-w-[1500px] mx-auto px-4 md:px-[4rem] xl:px-[12rem] pt-16 pb-16'>
				{children}
			</div>

			<section className='absolute left-0 right-0 bottom-0 flex-col pb-2 mx-auto max-w-[1500px] font-outfitBold flex items-center justify-center bg-secondary/70 backdrop-blur-lg px-4 md:px-[4rem] xl:px-[12rem]'>
				<div className='w-full min-h-[50px] h-auto rounded-3xl bg-card px-2 py-2 flex items-end justify-between'>
					<TooltipProvider>
						<Tooltip>
							<TooltipTrigger aria-label='Upload photo'>
								<div className='w-10 h-10 flex items-center justify-center rounded-full bg-card/70 cursor-pointer'>
									<ImagePlus size={20} className='mr-2' />
								</div>
							</TooltipTrigger>
							<TooltipContent>
								<p>Upload photo</p>
							</TooltipContent>
						</Tooltip>
					</TooltipProvider>

					<div
						ref={inputRef}
						className='w-full overflow-auto bg-transparent my-auto max-h-[100px] min-h-[20px] h-auto placeholder-foreground focus:outline-none font-outfitRegular'
						contentEditable
					>
						{/* {message} */}
					</div>

					<div
						className={twMerge(
							"w-10 h-10 flex items-center justify-center rounded-full bg-card/70 cursor-pointer",
							message.length === 0 && "opacity-35 cursor-not-allowed"
						)}
						onClick={handleSendMessage}
					>
						<SendHorizonal size={20} />
					</div>
				</div>

				<small className='font-outfitLight mt-2 text-center'>
					RasenganAI can make mistakes. Please make sure to verify the important
					information.
				</small>
			</section>
		</main>
	);
}
